import { InputForm } from '@/components/new/form/InputForm'
import { useModalWizardTabsContext } from '@/components/new/modalWizardTabs/ModalWizardTabsContext'
import { useModalWizardStepsContext } from '@/components/new/modalWizardSteps/ModalWizardStepsContext'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { IContainer } from '../../../interfaces/interfaces'
import { useEffect, useState } from 'react'
import { Label } from '@/components/ui/label'
import useContainerWizzardSteps from '@/state/useContainerWizzardSteps'
import SpinnerLoading from '@/components/misc/SpinnerLoading'
import { AsyncSelectForm } from '@/components/forms/AsyncSelectForm'
import { SelectFormUsers } from '@/containers/logistic/vehicles/components/SelectFormUsers'
import { CheckCircleIcon, CircleXIcon } from 'lucide-react'
import APIServicesV2 from '@/api/APIServicesV2'
import APIServices from '@/api/APIServices'
import { toast } from 'react-toastify'
import { getUsers } from '@/api/usersAPI'

export default function AseguramientoContainerTab({
  isEditing,
  editContainer,
}: {
  isEditing?: boolean
  editContainer?: IContainer
}) {
  const context = isEditing
    ? useModalWizardTabsContext()
    : useModalWizardStepsContext()
  const { useFormWizzard } = context
  const { control, setValue, getValues, watch } = useFormWizzard

  const data = useContainerWizzardSteps((state) => state.data)
  const updateData = useContainerWizzardSteps((state) => state.updateData)
  const queryClient = useQueryClient()

  // Query for container logistics data
  const { data: containerLogistics, isLoading } = useQuery({
    queryKey: ['container_logistics', editContainer?.id],
    queryFn: async () => {
      if (!editContainer?.id) return null
      const response = await APIServicesV2.get(`/containers/${editContainer.id}/logistics`)
      return response.data
    },
    enabled: !!editContainer?.id && isEditing,
    staleTime: 5 * 60 * 1000,
  })

  // Async fetchers for AsyncSelectForm
  const fetchTransportCompanies = async (search: string): Promise<{ id: string; label: string }[]> => {
    try {
      const response = await APIServices.get('/cargo/company', {
        params: {
          page: 1,
          per_page: 10,
          search: search || '',
          type: 'TRANSPORTER'
        }
      })
      
      const items = response.data?.items || []
      return items.map(({ id, name }: { id: number; name: string }) => ({
        id: String(id),
        label: name,
      }))
    } catch (error) {
      console.error('Error fetching transport companies:', error)
      return []
    }
  }

  const fetchStorageCompanies = async (search: string): Promise<{ id: string; label: string }[]> => {
    try {
      const response = await APIServices.get('/cargo/company', {
        params: {
          page: 1,
          per_page: 10,
          search: search || '',
          type: 'STORAGE'
        }
      })
      
      const items = response.data?.items || []
      return items.map(({ id, name }: { id: number; name: string }) => ({
        id: String(id),
        label: name,
      }))
    } catch (error) {
      console.error('Error fetching storage companies:', error)
      return []
    }
  }

  const fetchVehicles = async (search: string): Promise<{ id: string; label: string }[]> => {
    try {
      const response = await APIServices.get('/cargo/vehicle', {
        params: {
          page: 1,
          per_page: 10,
          search: search || '',
        }
      })
      
      const items = response.data?.items || []
      return items.map(({ id, name }: { id: number; name: string }) => ({
        id: String(id),
        label: name,
      }))
    } catch (error) {
      console.error('Error fetching vehicles:', error)
      return []
    }
  }

  const fetchTransportedBy = async (search: string): Promise<{ id: string; label: string }[]> => {
    try {
      const response = await APIServices.get('/cargo/company', {
        params: {
          page: 1,
          per_page: 10,
          search: search || '',
          type: 'TRANSPORTER'
        }
      })
      
      const items = response.data?.items || []
      return items.map(({ id, name }: { id: number; name: string }) => ({
        id: String(id),
        label: name,
      }))
    } catch (error) {
      console.error('Error fetching transported by companies:', error)
      return []
    }
  }

  // Mutation for updating container logistics
  const updateContainerLogistics = useMutation({
    mutationFn: async (logisticsData: {
      transporterCompanyId?: number | null
      storageCompanyId?: number | null
      vehicleId?: number | null
      transportedBy?: number | null
    }) => {
      if (!editContainer?.id) throw new Error('Container ID is required')
      const response = await APIServicesV2.patch(`/containers/${editContainer.id}/logistics`, logisticsData)
      return response.data
    },
    onSuccess: () => {
      toast.success('Información de aseguramiento actualizada correctamente')
      queryClient.invalidateQueries({ queryKey: ['container_logistics', editContainer?.id] })
    },
    onError: (error: any) => {
      console.error('Error updating container logistics:', error)
      toast.error(error?.response?.data?.message || 'Error al actualizar la información de aseguramiento')
    },
  })

  // Set form values when container logistics are loaded
  useEffect(() => {
    if (containerLogistics) {
      // Para AsyncSelectForm, solo necesitamos el ID
      setValue('transporterCompany', containerLogistics.transporterCompany?.id 
        ? String(containerLogistics.transporterCompany.id)
        : null
      );
      setValue('storageCompany', containerLogistics.storageCompany?.id 
        ? String(containerLogistics.storageCompany.id)
        : null
      );
      setValue('vehicle', containerLogistics.vehicle?.id 
        ? String(containerLogistics.vehicle.id)
        : null
      );
      setValue('transportedBy', containerLogistics.transportedBy
        ? { 
            id: containerLogistics.transportedBy.id, 
            displayName: containerLogistics.transportedBy.displayName,
            email: containerLogistics.transportedBy.email || ''
          }
        : null
      );
    }
  }, [containerLogistics, setValue])

  // Watch form changes and update data - using a single useEffect to avoid infinite loops
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name) {
        updateData({ ...data, [name]: value[name] })
      }
    })
    
    return () => subscription.unsubscribe()
  }, [watch, updateData])

  // Watch transporter company to conditionally show vehicle and transportedBy fields
  const transporterCompany = watch('transporterCompany')
  const showVehicleAndTransportedBy = transporterCompany && (
    (typeof transporterCompany === 'string' && transporterCompany !== '') ||
    (typeof transporterCompany === 'object' && transporterCompany !== null && transporterCompany.id)
  )

  // Get vehicle information for display
  const selectedVehicle = watch('vehicle')
  const vehicleInfo = containerLogistics?.vehicle

  // Query for users data for SelectFormUsers
  const { data: containerUsers, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['container_users'],
    queryFn: async () => {
      const response = await getUsers({
        page: 1,
        per_page: 100,
        search: '',
      })
      return response
    },
    staleTime: 5 * 60 * 1000,
  })



  // Function to handle saving logistics data
  const handleSaveLogistics = async () => {
    const formValues = getValues()
    
    const logisticsData: {
      transporterCompanyId?: number | null
      storageCompanyId?: number | null
      vehicleId?: number | null
      transportedBy?: number | null
    } = {}

    // Include all fields, even if null, to match the expected API structure
    // Handle both object format and string format for AsyncSelectForm fields
    logisticsData.transporterCompanyId = formValues.transporterCompany 
      ? (typeof formValues.transporterCompany === 'string' 
          ? Number(formValues.transporterCompany) 
          : Number(formValues.transporterCompany.id))
      : null
    logisticsData.storageCompanyId = formValues.storageCompany 
      ? (typeof formValues.storageCompany === 'string' 
          ? Number(formValues.storageCompany) 
          : Number(formValues.storageCompany.id))
      : null
    logisticsData.vehicleId = formValues.vehicle 
      ? (typeof formValues.vehicle === 'string' 
          ? Number(formValues.vehicle) 
          : Number(formValues.vehicle.id))
      : null
    logisticsData.transportedBy = formValues.transportedBy?.id 
      ? Number(formValues.transportedBy.id) 
      : null

    await updateContainerLogistics.mutateAsync(logisticsData)
  }

  // Expose the save function to the parent component
  useEffect(() => {
    if (isEditing) {
      // @ts-ignore - Adding custom property to window for parent access
      window.handleSaveAseguramiento = handleSaveLogistics
      // @ts-ignore - Expose loading state
      window.isSavingAseguramiento = updateContainerLogistics.isPending
    }
    
    return () => {
      // @ts-ignore
      delete window.handleSaveAseguramiento
      // @ts-ignore
      delete window.isSavingAseguramiento
    }
  }, [isEditing, handleSaveLogistics, updateContainerLogistics.isPending])

  return (
    <div className={'h-[60dvh] overflow-y-auto'}>
      {isLoading && isEditing ? (
        <div className="flex h-full w-full items-center justify-center">
          <SpinnerLoading />
        </div>
      ) : (
      <div
        className={'space-y-5 p-4'}
        style={isEditing ? { marginTop: '-.5rem' } : {}}
      >
        {/* Transportista */}
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <AsyncSelectForm
              name="transporterCompany"
              control={control}
              label="Transportista"
              fetcher={fetchTransportCompanies}
              placeholder="Buscar transportista..."
              notFoundMessage="No se encontraron transportistas."
            />
          </div>
        </div>

        {/* Almacenaje */}
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <AsyncSelectForm
              name="storageCompany"
              control={control}
              label="Almacenaje"
              fetcher={fetchStorageCompanies}
              placeholder="Buscar almacenaje..."
              notFoundMessage="No se encontraron almacenes."
            />
          </div>
        </div>

        {/* Vehículo - Solo se muestra cuando hay transportista seleccionado */}
        {showVehicleAndTransportedBy && (
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <AsyncSelectForm
                name="vehicle"
                control={control}
                label="Vehículo"
                fetcher={fetchVehicles}
                placeholder="Buscar vehículo..."
                notFoundMessage="No se encontraron vehículos."
              />
            </div>
          </div>
        )}

        {/* Transportado por - Solo se muestra cuando hay transportista seleccionado */}
        {showVehicleAndTransportedBy && (
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <SelectFormUsers
                name="transportedBy"
                control={control}
                label="Transportado por"
                data={
                  containerUsers?.map(({ id, displayName, email }) => ({
                    id: id,
                    title: `${displayName}`,
                    subtitle: email,
                  })) ?? []
                }
              />
            </div>
          </div>
        )}

        {/* Información del vehículo - Solo se muestra cuando hay vehículo seleccionado */}
        {vehicleInfo && (
          <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700">Información del vehículo</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs text-gray-500">Identificación</Label>
                <p className="text-sm font-medium">{vehicleInfo.identification || 'No disponible'}</p>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Número oficial</Label>
                <p className="text-sm font-medium">{vehicleInfo.oficialNumber || 'No disponible'}</p>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Tipo</Label>
                <p className="text-sm font-medium">{vehicleInfo.type || 'No disponible'}</p>
              </div>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  )
}